import * as React from "react";
import { StatusPill } from "@hp/ui";
import type { AssistantSource } from "@hp/api-client";
import type { AppealChainStep } from "../data/types";
import { formatDate, humanizeEdgeType, humanizeForum } from "../lib/workbenchFormat";
import { SourceDocumentViewer } from "./SourceDocumentViewer";

type SummaryToken =
  | { kind: "link"; text: string; findingId: string }
  | { kind: "citation"; text: string; note: string; findingId: string };

/**
 * Wraps case numbers (any step already shown on this page) and pinpoint-
 * reference citations (a paragraph/Article/Section number the backend
 * found) as buttons that open that finding's own source document - a
 * citation to a case outside this chain stays plain text, since there's no
 * finding id here to open.
 */
function annotateSummary(
  text: string,
  tokens: SummaryToken[],
  onOpenSource: (findingId: string, label: string) => void,
): React.ReactNode {
  const found = tokens.filter((t) => t.text && text.includes(t.text));
  if (found.length === 0) return text;
  // Longest first, so a shorter quote can't half-match inside a longer one.
  const sorted = [...found].sort((a, b) => b.text.length - a.text.length);
  const pattern = new RegExp(`(${sorted.map((t) => t.text.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")).join("|")})`, "g");
  return text.split(pattern).map((part, i) => {
    const match = sorted.find((t) => t.text === part);
    if (!match) return <React.Fragment key={i}>{part}</React.Fragment>;
    return (
      <button
        key={i}
        type="button"
        title={match.kind === "citation" ? match.note : "Open source document"}
        onClick={() => onOpenSource(match.findingId, part)}
        className="cursor-pointer font-medium text-brand-700 underline decoration-dotted underline-offset-2 hover:decoration-solid"
      >
        {part}
      </button>
    );
  });
}

const OUTCOME_CLASSES: Record<string, string> = {
  allowed: "bg-emerald-50 text-emerald-700 ring-emerald-600/20",
  disallowed: "bg-red-50 text-red-700 ring-red-600/20",
  partial: "bg-amber-50 text-amber-800 ring-amber-600/20",
  remanded: "bg-sky-50 text-sky-700 ring-sky-600/20",
  no_finding: "bg-slate-100 text-slate-700 ring-slate-600/20",
};

export interface DisputeThreadTimelineProps {
  steps: AppealChainStep[];
  /** The Finding id the viewer navigated in on, highlighted in the chain. */
  highlightFindingId?: string;
}

/**
 * Vertical timeline of a Finding's appeal chain: one node per step, oldest
 * at top, connected by the CitationEdge type that moved the matter from one
 * forum to the next. Renders correctly for a single-step chain (no
 * connector at all) and for chains of 3+ steps.
 */
export function DisputeThreadTimeline({ steps, highlightFindingId }: DisputeThreadTimelineProps) {
  const [viewerSource, setViewerSource] = React.useState<AssistantSource | null>(null);
  const openSource = (findingId: string, label: string) =>
    setViewerSource({ type: "finding", id: findingId, label });

  // Every step's own case number is a finding already fetched on this page,
  // so a mention of it inside another step's summary can open its source
  // document directly.
  const caseLinks = steps.map((s) => ({
    caseNumber: s.finding.case_number,
    findingId: s.finding.id,
    hasSourceDocument: !!s.finding.has_source_document,
  }));
  // A finding with no ingested source document has nothing for the viewer
  // to open, so any mention of it (case number or citation) must stay
  // plain text rather than a link that opens to an empty state.
  const hasSourceById = new Map(caseLinks.map((l) => [l.findingId, l.hasSourceDocument]));
  function summaryTokensFor(entry: {
    plain_summary_citations?: { quote: string; note: string; source_finding_id: string }[];
  }): SummaryToken[] {
    return [
      ...caseLinks
        .filter((l) => l.hasSourceDocument)
        .map((l): SummaryToken => ({ kind: "link", text: l.caseNumber, findingId: l.findingId })),
      ...(entry.plain_summary_citations ?? [])
        .filter((c) => hasSourceById.get(c.source_finding_id))
        .map(
          (c): SummaryToken => ({ kind: "citation", text: c.quote, note: c.note, findingId: c.source_finding_id }),
        ),
    ];
  }

  if (steps.length === 0) {
    return <p className="text-sm text-slate-400">No chain data available for this finding.</p>;
  }

  return (
    <>
    <ol className="relative flex flex-col">
      {steps.map((step, index) => {
        const isLast = index === steps.length - 1;
        const isHighlighted = step.finding.id === highlightFindingId;
        return (
          <li key={step.finding.id} className="relative flex gap-4 pb-8 last:pb-0">
            <div className="flex flex-col items-center">
              <span
                className={[
                  "z-10 flex h-8 w-8 shrink-0 items-center justify-center rounded-full border-2 text-xs font-semibold",
                  isHighlighted
                    ? "border-brand-600 bg-brand-600 text-white"
                    : "border-slate-300 bg-white text-slate-500",
                ].join(" ")}
              >
                {index + 1}
              </span>
              {!isLast ? <span className="w-px flex-1 bg-slate-200" aria-hidden /> : null}
            </div>

            <div className="flex-1 pt-0.5">
              {index > 0 ? (
                <div className="mb-2 inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-medium text-slate-600">
                  <span aria-hidden>↳</span>
                  {step.incoming_edge_type ? humanizeEdgeType(step.incoming_edge_type) : "Related"}
                </div>
              ) : null}

              <div
                className={[
                  "rounded-lg border bg-white p-4 shadow-sm",
                  isHighlighted ? "border-brand-300 ring-1 ring-brand-200" : "border-slate-200",
                ].join(" ")}
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    {step.finding.has_source_document ? (
                      <button
                        type="button"
                        title="Open source document"
                        onClick={() => openSource(step.finding.id, step.finding.case_number)}
                        className="cursor-pointer font-mono font-medium tabular-nums text-slate-900 underline decoration-dotted underline-offset-2 hover:decoration-solid"
                      >
                        {step.finding.case_number}
                      </button>
                    ) : (
                      <span className="font-mono font-medium tabular-nums text-slate-900">
                        {step.finding.case_number}
                      </span>
                    )}
                    <span className="ml-2 text-xs text-slate-400">{formatDate(step.finding.decision_date)}</span>
                  </div>
                  {step.authority_status ? (
                    <StatusPill kind="authority_status" status={step.authority_status.status} className="flex-none" />
                  ) : (
                    <span className="flex-none text-xs text-slate-400">Authority status unavailable</span>
                  )}
                </div>
                <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-slate-500">
                  <span>{humanizeForum(step.finding.forum)}</span>
                  <span aria-hidden>·</span>
                  <span
                    className={[
                      "inline-flex items-center rounded-full px-2 py-0.5 font-medium ring-1 ring-inset",
                      OUTCOME_CLASSES[step.finding.outcome] ?? OUTCOME_CLASSES.no_finding,
                    ].join(" ")}
                  >
                    {step.finding.outcome}
                  </span>
                </div>
                <p className="mt-1 text-xs text-slate-400">
                  {step.finding.petitioner} v. {step.finding.respondent}
                </p>
                <div className="mt-3 grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div className="min-w-0">
                    <p className="text-xs text-slate-600">
                      {annotateSummary(step.finding.ratio_summary, summaryTokensFor(step.finding), openSource)}
                    </p>
                  </div>
                  <div className="min-w-0 border-t border-slate-100 pt-3 md:border-l md:border-t-0 md:pl-6 md:pt-0">
                    <p className="text-[0.625rem] font-medium uppercase tracking-wide text-slate-400">Summary</p>
                    <p className="mt-1 text-xs text-slate-700">
                      {step.finding.plain_summary
                        ? annotateSummary(step.finding.plain_summary, summaryTokensFor(step.finding), openSource)
                        : "No additional plain-English detail available for this ruling."}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </li>
        );
      })}
    </ol>
    {viewerSource ? <SourceDocumentViewer source={viewerSource} onClose={() => setViewerSource(null)} /> : null}
    </>
  );
}
